import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { AbstractControl, FormControl, Validators } from '@angular/forms';
import { TypedDictionary } from '@core/utils';
import { BookerMultiCityStore } from '@dynamic-components/booker-v2/bookers/air/booker-air-multi-city/store/booker-multi-city.store';
import {
  defaultDateInputFormats,
  displayFormat,
} from '@dynamic-components/booker-v2/utils/date-formats';
import { BookerFlight } from '@shared/booker/types/booker-flight.type';
import {
  BookerResponse,
  isAfterMaxDate,
  isBeforeMinDate,
  isValidDate,
  moveFocus,
} from '@shared/index';
import { PairedRecentAirportSearches } from '@store/booker/types/booker-selector.types';
import { applyFilters, isCityPairRestricted } from '@store/origins/api';
import { OriginsFacade } from '@store/origins/origins.facade';
import { Airport, CountryWithAirports, Region } from '@store/origins/types';
import { ScheduleExtensionFacade } from '@store/schedule-extension/schedule-extension.facade';
import { addMonths, startOfToday } from 'date-fns';
import { ObservableInput } from 'ngx-observable-input';
import { equals, flatten, values } from 'ramda';
import {
  combineLatest,
  merge as rxMerge,
  Observable,
  of,
  Subscription,
} from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  startWith,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import { isSeasonalRoute } from '../city-pair-selector/city-pair-selector.utils';

@Component({
  selector: 'dot-multi-city-row',
  templateUrl: './multi-city-row.component.html',
  styleUrls: ['./multi-city-row.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MultiCityRowComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() content: BookerResponse;
  @Input() autoPrePopulateOrigin = false;
  @Input() regions: TypedDictionary<Region>;
  @Input() showValidationErrors = false;
  @Input() separatePartnerCities: boolean;
  @Input() recentAirports: PairedRecentAirportSearches;
  @Input() formWrapper;
  @ObservableInput() @Input('origins') origins$: Observable<
    CountryWithAirports[]
  >;

  @Input() set index(index: number) {
    this.minDate$ = this.bookerMultiCityStore.minDate$(index);
    this.idx = index;
  }

  @Input() set flight(flightControl: AbstractControl<BookerFlight>) {
    this.toAirport = flightControl.get('toAirport');
    this.fromAirport = flightControl.get('fromAirport');
    this.departure = flightControl.get('departureDate');
  }

  @Output() eitherAirportChange = new EventEmitter<{
    origin: Airport;
    destination: Airport;
  }>();

  idx: number;
  minDate: Date = startOfToday();
  minDate$: Observable<Date>;
  maxDate: Date = addMonths(startOfToday(), 12);
  fromAirport: AbstractControl<Airport> = new FormControl();
  toAirport: AbstractControl<Airport> = new FormControl();
  departure: AbstractControl<Date> = new FormControl();
  displayFormat = displayFormat;
  customDateInputFormats = [this.displayFormat, ...defaultDateInputFormats];
  destinations$: Observable<CountryWithAirports[]>;
  seasonalRouteMessage = '';
  scheduleExtensionMsg = '';
  isInitialized = false;

  @ViewChild('jbDatePickerInput', { static: true, read: ElementRef })
  jbDatePickerInput: ElementRef;

  private subscriptions: Subscription = new Subscription();

  constructor(
    private readonly bookerMultiCityStore: BookerMultiCityStore,
    private originsFacade: OriginsFacade,
    public scheduleExtensionFacade: ScheduleExtensionFacade,
  ) {}

  getErrorMessage(errors: { [key: string]: string }): string {
    return Object.entries(errors)[0][1] || '';
  }

  ngOnInit() {
    const flight$ = this.bookerMultiCityStore
      .flight$(this.idx)
      .pipe(filter(x => !!x));

    this.subscriptions.add(
      this.scheduleExtensionFacade.extensionDate.subscribe(
        scheduleExtension => {
          if (scheduleExtension.date) {
            this.maxDate = scheduleExtension.date;
            this.scheduleExtensionMsg = scheduleExtension.label;

            if (this.idx < 2) {
              // for first 2 rows of multi city booker, flights and date are always required
              this.setRowValidators();
            } else {
              // for additional rows of multi city booker, row can be left blank
              // but all fields are required if any one of them has been populated
              this.subscriptions.add(
                combineLatest([flight$, this.minDate$])
                  .pipe(
                    tap(([_, minDate]) => {
                      this.minDate = minDate;
                    }),
                    switchMap(([flights, _]) =>
                      combineLatest([
                        this.formControlHasValue(flights, 'fromAirport'),
                        this.formControlHasValue(flights, 'toAirport'),
                        this.formControlHasValue(flights, 'departureDate'),
                      ]),
                    ),
                    map(haveValues => haveValues.some(hasValue => !!hasValue)),
                  )
                  .subscribe(rowHasValues => {
                    if (rowHasValues) {
                      this.setRowValidators();
                    } else {
                      this.clearRowValidators();
                    }
                    this.departure.updateValueAndValidity();
                    this.fromAirport.updateValueAndValidity();
                    this.toAirport.updateValueAndValidity();
                  }),
              );
            }
          }
        },
      ),
    );

    this.destinations$ = rxMerge(
      this.originsFacade
        .destinationAirports(`MULTI_ROW_${this.idx}`)
        .pipe(
          map(destinations => applyFilters(destinations, false, false, [])),
        ),
      this.fromAirport.valueChanges.pipe(filter(value => !value)),
    ).pipe(
      switchMap((destinations: any) =>
        destinations?.length ? of(destinations) : this.origins$,
      ),
    );

    this.subscriptions.add(
      this.fromAirport.valueChanges
        .pipe(
          filter(value => !value),
          withLatestFrom(this.fromAirport.valueChanges),
          filter(([_, from]) => !!from),
        )
        .subscribe(([_, from]) => {
          this.originsFacade.requestDestinationAirports(
            from,
            `MULTI_ROW_${this.idx}`,
          );
        }),
    );

    this.subscriptions.add(
      this.destinations$
        .pipe(
          withLatestFrom(this.toAirport.valueChanges),
          filter(
            ([destinations, destination]) => !!destinations && !!destination,
          ),
          switchMap(([destinations, destination]) => {
            const updatedDestination = flatten(
              applyFilters(destinations, false, false, []).map(
                country => country.airports,
              ),
            ).find(d => d.code === destination.code);
            return of(updatedDestination);
          }),
          filter(Boolean),
        )
        .subscribe((updatedDestination: Airport) => {
          if (!equals(this.toAirport.value, updatedDestination)) {
            this.toAirport.patchValue(updatedDestination);
          }
        }),
    );

    this.subscriptions.add(
      isSeasonalRoute(this.fromAirport as any, this.toAirport as any).subscribe(
        isSeasonal => {
          this.seasonalRouteMessage = isSeasonal
            ? this.content.bookMessages.seasonalRouteMessage
            : '';
        },
      ),
    );
  }

  ngAfterViewInit() {
    this.isInitialized = true;
  }

  handleCityPairChange(
    airport: Airport,
    changedAirport: 'origin' | 'destination',
  ) {
    if (changedAirport === 'origin') {
      const destinationInvalid = isCityPairRestricted(
        airport?.code,
        this.toAirport.value?.code,
      );

      if (destinationInvalid) {
        this.toAirport.setValue(undefined);
      }
    }

    if (changedAirport === 'destination') {
      const originInvalid = isCityPairRestricted(
        airport?.code,
        this.fromAirport.value?.code,
      );

      if (originInvalid) {
        this.fromAirport.setValue(undefined);
      }
    }
  }

  removeRow(idx: number) {
    this.bookerMultiCityStore.removeFlight(idx);
  }

  formControlHasValue(
    form: AbstractControl,
    controlName: string,
  ): Observable<boolean> {
    return form.get(controlName).valueChanges.pipe(
      map(value => !!value),
      startWith(false),
      distinctUntilChanged(),
    );
  }

  clearRowValidators() {
    this.fromAirport.setValidators([]);
    this.toAirport.setValidators([]);
    this.departure.setValidators([]);
    this.departure.updateValueAndValidity();
    this.fromAirport.updateValueAndValidity();
    this.toAirport.updateValueAndValidity();
  }

  setRowValidators() {
    const departValidators = {
      isValidDate: isValidDate(
        this.content.dateAggregator.errors.inValidDateError,
      ),
      isBeforeMinDate: isBeforeMinDate(
        this.minDate,
        this.content.dateAggregator.errors.beforeMinDateError,
      ),
      isAfterMaxDate: isAfterMaxDate(
        this.maxDate,
        this.content.dateAggregator.errors.afterMaxDateError,
      ),
    };
    this.departure.setValidators(values(departValidators));
    this.fromAirport.setValidators([Validators.required]);
    this.toAirport.setValidators([Validators.required]);
    this.departure.updateValueAndValidity();
    this.fromAirport.updateValueAndValidity();
    this.toAirport.updateValueAndValidity();
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  handleOriginChange($event: Airport) {
    this.fromAirport.setValue($event);
    this.handleCityPairChange($event, 'origin');
  }

  handleDestinationChange($event: Airport) {
    this.toAirport.setValue($event);
    this.handleCityPairChange($event, 'destination');
  }

  dateValueChanged($event: Date) {
    if (
      $event &&
      this.isInitialized &&
      $event.getTime() !== this.departure.value?.getTime()
    ) {
      // Only call move focus if value is present and component is initialized.
      // We don't want to run it on application load.
      setTimeout(
        () =>
          moveFocus(
            this.formWrapper,
            this.jbDatePickerInput.nativeElement.querySelector('input'),
            true,
          ),
        250,
      );
    }
  }
}
